/**
 * Event search, country and state filters, and the hide/show toggles.
 * Stateless: the filter values live in EventSelection.
 */

import React from 'react';
import { Checkbox, Dropdown, Form } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import { EventFilters } from '../types';

interface EventFilterBarProps {
  filters: EventFilters;
  countryOptions: string[];
  stateOptions: string[];
  singleOrganizationCount: number;
  alreadyAddedCount: number;
  onChange: <K extends keyof EventFilters>(
    key: K,
    value: EventFilters[K]
  ) => void;
}

const toOptions = (anyText: string, values: string[]) => [
  { key: '', value: '', text: anyText },
  ...values.map((value) => ({ key: value, value, text: value }))
];

const EventFilterBar: React.FC<EventFilterBarProps> = ({
  filters,
  countryOptions,
  stateOptions,
  singleOrganizationCount,
  alreadyAddedCount,
  onChange
}) => (
  <Form>
    <Form.Group widths="equal">
      <Form.Input
        label={I18n.t(i18nKeys.EVENTS.SEARCH_LABEL)}
        icon="search"
        placeholder={I18n.t(i18nKeys.EVENTS.SEARCH_PLACEHOLDER)}
        value={filters.text}
        onChange={(_, { value }) => onChange('text', value)}
      />
      <Form.Field>
        <label htmlFor="add-organization-country">
          <Translate value={i18nKeys.EVENTS.COUNTRY} />
        </label>
        <Dropdown
          id="add-organization-country"
          aria-label={I18n.t(i18nKeys.EVENTS.COUNTRY)}
          selection
          search
          value={filters.country}
          options={toOptions(
            I18n.t(i18nKeys.EVENTS.ANY_COUNTRY),
            countryOptions
          )}
          onChange={(_, { value }) => onChange('country', String(value ?? ''))}
        />
      </Form.Field>
      {filters.country && (
        <Form.Field>
          <label htmlFor="add-organization-state">
            <Translate value={i18nKeys.EVENTS.STATE} />
          </label>
          <Dropdown
            id="add-organization-state"
            aria-label={I18n.t(i18nKeys.EVENTS.STATE)}
            selection
            search
            value={filters.state}
            options={toOptions(I18n.t(i18nKeys.EVENTS.ANY_STATE), stateOptions)}
            onChange={(_, { value }) => onChange('state', String(value ?? ''))}
          />
        </Form.Field>
      )}
    </Form.Group>
    <Form.Group inline>
      <Form.Field>
        <Checkbox
          label={I18n.t(i18nKeys.EVENTS.UPCOMING_ONLY)}
          checked={filters.upcomingOnly}
          onChange={(_, { checked }) => onChange('upcomingOnly', !!checked)}
        />
      </Form.Field>
      <Form.Field>
        <Checkbox
          label={I18n.t(i18nKeys.EVENTS.HIDE_CLOSED)}
          checked={filters.hideClosed}
          onChange={(_, { checked }) => onChange('hideClosed', !!checked)}
        />
      </Form.Field>
      <Form.Field>
        <Checkbox
          label={I18n.t(i18nKeys.EVENTS.HIDE_SINGLE_ORGANIZATION, {
            count: singleOrganizationCount
          })}
          checked={filters.hideSingleOrganization}
          onChange={(_, { checked }) =>
            onChange('hideSingleOrganization', !!checked)
          }
        />
      </Form.Field>
      {alreadyAddedCount > 0 && (
        <Form.Field>
          <Checkbox
            label={I18n.t(i18nKeys.EVENTS.SHOW_ALREADY_ADDED, {
              count: alreadyAddedCount
            })}
            checked={filters.showAlreadyAdded}
            onChange={(_, { checked }) =>
              onChange('showAlreadyAdded', !!checked)
            }
          />
        </Form.Field>
      )}
    </Form.Group>
  </Form>
);

export default EventFilterBar;
